import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ACTION_STATUS } from "@/lib/status";
import { Plus, Wand2, CheckCircle2, AlertTriangle } from "lucide-react";

const DAYS = [["0", "Po"], ["1", "Út"], ["2", "St"], ["3", "Čt"], ["4", "Pá"], ["5", "So"], ["6", "Ne"]];

const pickTexts = (a) => ({ instructions: a.instructions || "", confirmation_text: a.confirmation_text || "", flyer_headline: a.flyer_headline || "", flyer_intro: a.flyer_intro || "", flyer_dates: a.flyer_dates || "", admin_only_from: a.admin_only_from || "", any_entrance: !!a.any_entrance });

function ActionTextsCard({ action, reload }) {
  const [texts, setTexts] = useState(pickTexts(action));
  useEffect(() => setTexts(pickTexts(action)), [action]);
  const saveTexts = async () => {
    try { await api.patch(`/actions/${action.id}/texts`, texts); toast.success("Texty pro obyvatele uloženy"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
      <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="action-texts">
        <h3 className="font-semibold text-[#0A2540]">Texty pro obyvatele</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Pokyny (zobrazí se v úvodu rezervace)</Label>
            <Textarea rows={7} value={texts.instructions} onChange={(e) => setTexts({ ...texts, instructions: e.target.value })} placeholder={"Prosíme zaregistrujte se…\nKontakty na výbor…"} data-testid="action-instructions" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Text po potvrzení rezervace (obrazovka i e-mail)</Label>
            <Textarea rows={7} value={texts.confirmation_text} onChange={(e) => setTexts({ ...texts, confirmation_text: e.target.value })} placeholder="Rezervace je potvrzena. Změnu termínu řešte s předsedou SVJ nebo společností Okenfix." data-testid="action-confirmation-text" />
          </div>
        </div>
        <div className="border-t pt-3 space-y-2">
          <div className="text-sm font-medium text-[#0A2540]">Leták pro obyvatele (PDF s QR kódem) a omezení termínů</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Nadpis letáku</Label><Input value={texts.flyer_headline} onChange={(e) => setTexts({ ...texts, flyer_headline: e.target.value })} placeholder="Servis oken – rezervace termínu" data-testid="action-flyer-headline" /></div>
            <div className="space-y-1"><Label className="text-xs">Termíny (text)</Label><Input value={texts.flyer_dates} onChange={(e) => setTexts({ ...texts, flyer_dates: e.target.value })} placeholder="automaticky z harmonogramu" data-testid="action-flyer-dates" /></div>
            <div className="space-y-1"><Label className="text-xs">Termíny jen pro předsedu od (obyvatelé je nevidí)</Label><Input type="date" value={texts.admin_only_from} onChange={(e) => setTexts({ ...texts, admin_only_from: e.target.value })} data-testid="action-admin-only-from" /></div>
            <label className="flex items-center gap-2 text-sm pt-1"><input type="checkbox" checked={texts.any_entrance} onChange={(e) => setTexts({ ...texts, any_entrance: e.target.checked })} data-testid="action-any-entrance" /> Obyvatelé všech vchodů mohou rezervovat kterýkoliv termín (bez omezení na vchod)</label>
            <div className="space-y-1 md:col-span-2"><Label className="text-xs">Úvodní text letáku</Label><Textarea rows={2} value={texts.flyer_intro} onChange={(e) => setTexts({ ...texts, flyer_intro: e.target.value })} placeholder="V domě … proběhne servis oken a lodžií…" data-testid="action-flyer-intro" /></div>
          </div>
        </div>
        <Button size="sm" onClick={saveTexts} style={{ background: "#0A2540" }} data-testid="action-texts-save">Uložit texty</Button>
      </div>
  );
}

export default function ActionPlanner({ svj, actions, actionId, setActionId, reload, isSuper }) {
  const [creating, setCreating] = useState(false);
  const action = actions.find((a) => a.id === actionId);

  if (!isSuper) {
    const action = actions.find((a) => a.id === actionId) || actions[0];
    return (
      <div className="mt-4 space-y-4">
        <div className="bg-white rounded-xl border p-6 text-slate-600">Servisní akce a harmonogram spravuje OKENFIX. Texty pro obyvatele, leták a omezení termínů můžete upravit níže.</div>
        {action && <ActionTextsCard action={action} reload={reload} />}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#0A2540]">Servisní akce</h3>
        <Button onClick={() => setCreating(true)} style={{ background: "#0A2540" }} data-testid="create-action-btn"><Plus className="h-4 w-4 mr-1" /> Nová akce</Button>
      </div>
      {creating && <CreateAction svj={svj} onClose={() => setCreating(false)} onDone={(id) => { setCreating(false); reload().then?.(() => {}); setActionId?.(id); }} />}
      {action ? <PlannerBody svj={svj} action={action} reload={reload} /> : <div className="bg-white rounded-xl border p-6 text-slate-500">Vyberte nebo vytvořte akci.</div>}
    </div>
  );
}

function CreateAction({ svj, onClose, onDone }) {
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [sel, setSel] = useState(svj.entrances.map((e) => e.id));
  const submit = async () => {
    if (!name.trim() || sel.length === 0) return toast.error("Zadejte název a vyberte vchody");
    try {
      const { data } = await api.post(`/svjs/${svj.id}/actions`, { name, entrance_ids: sel, instructions });
      toast.success(`Akce vytvořena · ${data.included_apartments} bytů zahrnuto`);
      onDone(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-white rounded-xl border p-5 space-y-3">
      <Input placeholder="Název akce, např. Servis oken – říjen 2026" value={name} onChange={(e) => setName(e.target.value)} data-testid="action-name" />
      <div>
        <Label className="text-xs">Zahrnuté vchody</Label>
        <div className="flex gap-3 flex-wrap mt-1">
          {svj.entrances.map((e) => (
            <label key={e.id} className="flex items-center gap-2 text-sm border rounded px-3 py-1.5">
              <Checkbox checked={sel.includes(e.id)} onCheckedChange={(c) => setSel(c ? [...sel, e.id] : sel.filter((x) => x !== e.id))} /> Vchod {e.label}
            </label>
          ))}
        </div>
      </div>
      <Textarea placeholder="Pokyny obyvatelům" value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      <div className="flex gap-2"><Button variant="outline" onClick={onClose}>Zrušit</Button><Button onClick={submit} style={{ background: "#0A2540" }} data-testid="save-action">Vytvořit</Button></div>
    </div>
  );
}

function PlannerBody({ svj, action, reload }) {
  const [cfg, setCfg] = useState(action.config);
  const [teams, setTeams] = useState([]);
  const [plan, setPlan] = useState([]); // [{entrance_id, team_id, datesText}]
  const [preview, setPreview] = useState(null);
  const [startDate, setStartDate] = useState("2026-10-05");

  useEffect(() => { setCfg(action.config); }, [action.id]);
  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)); }, []);
  useEffect(() => {
    api.get(`/actions/${action.id}/plan-status`).then((r) => {
      const existing = r.data.plan?.length ? r.data.plan : r.data.draft_plan;
      const base = action.entrance_ids.map((eid) => {
        const found = (existing || []).find((p) => p.entrance_id === eid);
        return { entrance_id: eid, team_id: found?.team_id || "", datesText: (found?.dates || []).join(", ") };
      });
      setPlan(base);
      setCfg(r.data.config);
    });
  }, [action.id]);

  const entLabel = (eid) => svj.entrances.find((e) => e.id === eid)?.label || eid;
  const toPlan = () => plan.map((p) => ({ entrance_id: p.entrance_id, team_id: p.team_id || null, dates: parseDates(p.datesText) }));

  const saveConfig = async () => {
    try { await api.patch(`/actions/${action.id}`, { config: cfg }); toast.success("Nastavení uloženo"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const regenerate = async () => {
    if (!window.confirm("Smazat všechny volné termíny a vytvořit je znovu podle aktuálního nastavení? Obsazené termíny zůstanou.")) return;
    try {
      await api.patch(`/actions/${action.id}`, { config: cfg });
      const { data } = await api.post(`/actions/${action.id}/regenerate-slots`);
      toast.success(`Termíny přegenerovány: ${data.created} nových (${data.day_slots.map((s) => s.start).join(", ")})`); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doPreview = async () => {
    try { const { data } = await api.post(`/actions/${action.id}/preview-plan`, { plan: toPlan() }); setPreview(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doPublish = async () => {
    try { await api.post(`/actions/${action.id}/publish`, { plan: toPlan() }); toast.success("Harmonogram zveřejněn"); reload(); doPreview(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.preview) setPreview(d.preview);
      toast.error(d?.message || apiErr(e));
    }
  };
  const autoSuggest = async (eid) => {
    const p = plan.find((x) => x.entrance_id === eid);
    try {
      const { data } = await api.post(`/actions/${action.id}/suggest-plan`, { start_date: startDate, entrance_ids: [eid], team_ids: p?.team_id ? [p.team_id] : [] });
      const dates = data.plan[0]?.dates || [];
      setPlan((pl) => pl.map((x) => (x.entrance_id === eid ? { ...x, datesText: dates.join(", ") } : x)));
      toast.success(`Navrženo ${dates.length} dnů`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/actions/${action.id}/status`, { status }); toast.success("Stav změněn"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const num = (k, v) => setCfg({ ...cfg, [k]: parseInt(v, 10) || 0 });

  return (
    <div className="space-y-5">
      {/* status controls */}
      <div className="bg-white rounded-xl border p-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-slate-500">Stav akce:</span>
        <span className={`text-xs px-2 py-1 rounded border ${ACTION_STATUS[action.status]?.cls}`}>{ACTION_STATUS[action.status]?.label}</span>
        <div className="flex-1" />
        {action.status === "zverejneno" && <Button size="sm" variant="outline" onClick={() => setStatus("uzavreno")} data-testid="close-action">Uzavřít registrace</Button>}
        {action.status === "uzavreno" && <Button size="sm" variant="outline" onClick={() => setStatus("zverejneno")}>Znovu otevřít</Button>}
        {["zverejneno", "uzavreno"].includes(action.status) && <Button size="sm" variant="outline" onClick={() => setStatus("dokonceno")}>Označit dokončeno</Button>}
      </div>

      <ActionTextsCard action={action} reload={reload} />

      {/* config */}
      <div className="bg-white rounded-xl border p-5 space-y-4">
        <h4 className="font-semibold text-[#0A2540]">Plánovací nastavení</h4>
        <div>
          <Label className="text-xs">Pracovní dny (So/Ne povolte výslovně)</Label>
          <div className="flex gap-2 mt-1">
            {DAYS.map(([d, l]) => {
              const on = cfg.work_days?.includes(parseInt(d, 10));
              return <button key={d} onClick={() => { const n = parseInt(d, 10); setCfg({ ...cfg, work_days: on ? cfg.work_days.filter((x) => x !== n) : [...cfg.work_days, n] }); }}
                className={`w-11 h-10 rounded border text-sm font-medium ${on ? "bg-[#0A2540] text-white" : "bg-white"}`} data-testid={`day-${d}`}>{l}</button>;
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Časové okna návštěv</Label>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCfg({ ...cfg, fixed_starts: ["08:00", "10:00", "12:00", "14:00", "16:00"], visit_duration: 120 })}
              className={`px-3 h-9 rounded border text-sm ${(cfg.fixed_starts || []).length ? "bg-[#0A2540] text-white" : "bg-white"}`} data-testid="preset-2h">Po 2 hodinách: 8:00 · 10:00 · 12:00 · 14:00 · 16:00</button>
            <button onClick={() => setCfg({ ...cfg, fixed_starts: [] })}
              className={`px-3 h-9 rounded border text-sm ${(cfg.fixed_starts || []).length ? "bg-white" : "bg-[#0A2540] text-white"}`} data-testid="preset-continuous">Průběžně od začátku do konce</button>
          </div>
          {(cfg.fixed_starts || []).length > 0 && (
            <F label="Začátky návštěv (HH:MM, oddělené čárkou)">
              <Input value={(cfg.fixed_starts || []).join(", ")} onChange={(e) => setCfg({ ...cfg, fixed_starts: e.target.value.split(/[,\s]+/).map((s) => s.trim()).filter((s) => /^\d{1,2}:\d{2}$/.test(s)).map((s) => s.padStart(5, "0")) })} data-testid="fixed-starts" />
            </F>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <F label="Začátek"><Input value={cfg.work_start} onChange={(e) => setCfg({ ...cfg, work_start: e.target.value })} disabled={(cfg.fixed_starts || []).length > 0} /></F>
          <F label="Konec"><Input value={cfg.work_end} onChange={(e) => setCfg({ ...cfg, work_end: e.target.value })} disabled={(cfg.fixed_starts || []).length > 0} /></F>
          <F label="Délka návštěvy (min)"><Input type="number" value={cfg.visit_duration} onChange={(e) => num("visit_duration", e.target.value)} /></F>
          <F label="Rezerva mezi (min)"><Input type="number" value={cfg.buffer} onChange={(e) => num("buffer", e.target.value)} /></F>
          <F label="Limit bytů / tým / den"><Input type="number" value={cfg.daily_limit_per_team} onChange={(e) => num("daily_limit_per_team", e.target.value)} data-testid="daily-limit" /></F>
          <F label="Max. návštěv / den"><Input type="number" value={cfg.max_visits_per_day} onChange={(e) => num("max_visits_per_day", e.target.value)} /></F>
          <F label="Přestávka od"><Input value={cfg.breaks?.[0]?.start || ""} disabled={(cfg.fixed_starts || []).length > 0} onChange={(e) => setCfg({ ...cfg, breaks: [{ start: e.target.value, end: cfg.breaks?.[0]?.end || "12:30" }] })} /></F>
          <F label="Přestávka do"><Input value={cfg.breaks?.[0]?.end || ""} disabled={(cfg.fixed_starts || []).length > 0} onChange={(e) => setCfg({ ...cfg, breaks: [{ start: cfg.breaks?.[0]?.start || "12:00", end: e.target.value }] })} /></F>
        </div>
        <F label="Vyloučené dny (YYYY-MM-DD, oddělené čárkou)">
          <Input value={(cfg.excluded_dates || []).join(", ")} onChange={(e) => setCfg({ ...cfg, excluded_dates: parseDates(e.target.value) })} />
        </F>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={saveConfig} data-testid="save-config">Uložit nastavení</Button>
          {action.status === "zverejneno" && (
            <Button variant="outline" onClick={regenerate} data-testid="regenerate-slots">Přegenerovat volné termíny podle nastavení</Button>
          )}
        </div>
        {action.status === "zverejneno" && <p className="text-xs text-slate-500">Změna časových oken se u zveřejněné akce projeví až po „Přegenerovat volné termíny“ (obsazené termíny zůstávají).</p>}
      </div>

      {/* plan per entrance */}
      <div className="bg-white rounded-xl border p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="font-semibold text-[#0A2540]">Harmonogram vchodů</h4>
          <div className="flex items-end gap-2">
            <F label="Počáteční datum pro návrh"><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" /></F>
          </div>
        </div>
        {plan.map((p) => (
          <div key={p.entrance_id} className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="font-medium text-[#0A2540]">Vchod {entLabel(p.entrance_id)}</span>
              <div className="flex items-center gap-2">
                <Select value={p.team_id} onValueChange={(v) => setPlan(plan.map((x) => x.entrance_id === p.entrance_id ? { ...x, team_id: v } : x))}>
                  <SelectTrigger className="w-40" data-testid={`plan-team-${p.entrance_id}`}><SelectValue placeholder="Tým" /></SelectTrigger>
                  <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => autoSuggest(p.entrance_id)} data-testid={`suggest-${p.entrance_id}`}><Wand2 className="h-4 w-4 mr-1" /> Navrhnout dny</Button>
              </div>
            </div>
            <Input placeholder="Dny: 2026-10-05, 2026-10-06, …" value={p.datesText}
              onChange={(e) => setPlan(plan.map((x) => x.entrance_id === p.entrance_id ? { ...x, datesText: e.target.value } : x))}
              data-testid={`plan-dates-${p.entrance_id}`} />
          </div>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" onClick={doPreview} data-testid="preview-plan">Zkontrolovat kapacitu</Button>
          <Button onClick={doPublish} style={{ background: "#C5A059" }} data-testid="publish-plan">Zveřejnit harmonogram</Button>
        </div>
      </div>

      {preview && <PreviewBox preview={preview} entLabel={entLabel} />}
    </div>
  );
}

function PreviewBox({ preview, entLabel }) {
  return (
    <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="capacity-preview">
      <div className="flex items-center gap-2">
        {preview.can_publish ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <AlertTriangle className="h-5 w-5 text-amber-500" />}
        <h4 className="font-semibold text-[#0A2540]">Kontrola kapacity</h4>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-slate-50 rounded p-3"><div className="text-2xl font-bold text-[#0A2540]" data-testid="preview-apartments">{preview.total_apartments}</div><div className="text-xs text-slate-500">bytů</div></div>
        <div className="bg-slate-50 rounded p-3"><div className="text-2xl font-bold text-blue-700" data-testid="preview-capacity">{preview.total_capacity}</div><div className="text-xs text-slate-500">volných slotů</div></div>
        <div className="bg-slate-50 rounded p-3"><div className={`text-2xl font-bold ${preview.total_shortfall ? "text-red-600" : "text-emerald-600"}`} data-testid="preview-shortfall">{preview.total_shortfall}</div><div className="text-xs text-slate-500">chybí míst</div></div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-slate-500"><tr><th className="text-left">Vchod</th><th className="text-right">Byty</th><th className="text-right">Kapacita</th><th className="text-right">Chybí</th></tr></thead>
        <tbody>{preview.entrances.map((e) => (
          <tr key={e.entrance_id} className="border-t"><td className="py-1">Vchod {e.entrance_label}</td><td className="text-right">{e.apt_count}</td><td className="text-right">{e.capacity}</td>
            <td className={`text-right font-medium ${e.shortfall ? "text-red-600" : "text-emerald-600"}`}>{e.shortfall}</td></tr>
        ))}</tbody>
      </table>
      {preview.conflicts?.length > 0 && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{preview.conflicts.map((c) => <div key={c}>• {c}</div>)}</div>}
      {preview.suggestion && <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">{preview.suggestion}</div>}
      {!preview.can_publish && <div className="text-sm text-slate-500">Zveřejnění pro všechny byty není možné, dokud není kapacita dostatečná. Plán lze uložit jako rozpracovaný.</div>}
    </div>
  );
}

function parseDates(text) {
  return (text || "").split(/[,\n]/).map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
}
function F({ label, children }) { return <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>; }
